import React, { useState, useCallback } from 'react';
import { 
  Upload, 
  X, 
  FileText, 
  Settings, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Edit,
  Save,
  Users,
  Shield,
  Globe,
  Lock,
  Plus
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import type { 
  DocumentType, 
  DocumentAccessLevel, 
  DocumentOwnershipType
} from '../types/database';
import {
  getDocumentTypeLabel,
  getDocumentAccessLevelLabel,
  getDocumentOwnershipTypeLabel,
  formatFileSize
} from '../types/database';

interface BulkUploadFile {
  id: string;
  file: File;
  title: string;
  description: string;
  document_type: DocumentType;
  access_level: DocumentAccessLevel;
  ownership_type: DocumentOwnershipType;
  isUploading: boolean;
  uploadError?: string;
  uploadSuccess?: boolean;
}

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  buyerId?: string;
  dealId?: string;
}

export function BulkUploadModal({ 
  isOpen, 
  onClose, 
  onUploadSuccess,
  buyerId,
  dealId
}: BulkUploadModalProps) {
  const { user } = useUser();
  
  const [files, setFiles] = useState<BulkUploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Bulk editing state
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkDocumentType, setBulkDocumentType] = useState<DocumentType>('other');
  const [bulkAccessLevel, setBulkAccessLevel] = useState<DocumentAccessLevel>('private');
  const [bulkOwnershipType, setBulkOwnershipType] = useState<DocumentOwnershipType>('buyer');
  const [bulkDescription, setBulkDescription] = useState('');

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFilesToState(selectedFiles);
  }, []);

  // Add files to state
  const addFilesToState = useCallback((selectedFiles: File[]) => {
    const newFiles: BulkUploadFile[] = selectedFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      description: '',
      document_type: 'other' as DocumentType,
      access_level: 'private' as DocumentAccessLevel,
      ownership_type: 'buyer' as DocumentOwnershipType,
      isUploading: false
    }));

    setFiles(prev => [...prev, ...newFiles]);
    setError(null);
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFilesToState(droppedFiles);
    }
  }, [addFilesToState]);

  // Remove file from list
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Update individual file properties
  const updateFileProperty = useCallback((fileId: string, property: keyof BulkUploadFile, value: any) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, [property]: value } : f
    ));
  }, []);

  // Apply bulk properties to selected files
  const applyBulkProperties = useCallback(() => {
    setFiles(prev => prev.map(f => ({
      ...f,
      document_type: bulkDocumentType,
      access_level: bulkAccessLevel,
      ownership_type: bulkOwnershipType,
      description: bulkDescription || f.description
    })));
    setShowBulkEdit(false);
  }, [bulkDocumentType, bulkAccessLevel, bulkOwnershipType, bulkDescription]);

  // Upload all files
  const handleBulkUpload = async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setMessage(null);

    try {
      const uploadPromises = files.map(async (fileData) => {
        try {
          updateFileProperty(fileData.id, 'isUploading', true);

          // Route through backend to mirror existing single-file upload flow
          const formData = new FormData();
          formData.append('file', fileData.file);
          formData.append('title', fileData.title || fileData.file.name);
          if (fileData.description && fileData.description.trim()) {
            formData.append('description', fileData.description.trim());
          }
          formData.append('document_type', fileData.document_type);
          formData.append('access_level', fileData.access_level);
          formData.append('ownership_type', fileData.ownership_type);
          if (dealId) formData.append('deal_id', dealId);
          if (user?.id) formData.append('user_id', user.id);

          const baseUrl = (import.meta as any).env?.VITE_BACKEND_URL || '/api';
          const res = await fetch(`${baseUrl}/upload-document`, {
            method: 'POST',
            body: formData
          });

          let errorText: string | null = null;
          if (!res.ok) {
            try {
              const data = await res.json();
              errorText = data?.detail || 'Upload failed';
            } catch {
              errorText = `Upload failed (${res.status})`;
            }
          }

          if (!res.ok) {
            updateFileProperty(fileData.id, 'uploadError', errorText || 'Upload failed');
            return { success: false, error: errorText };
          }

          updateFileProperty(fileData.id, 'uploadSuccess', true);
          return { success: true };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed';
          updateFileProperty(fileData.id, 'uploadError', errorMessage);
          return { success: false, error: errorMessage };
        } finally {
          updateFileProperty(fileData.id, 'isUploading', false);
        }
      });

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        setMessage(`Successfully uploaded ${successCount} file(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        onUploadSuccess();
        
        // Clear successful uploads after a delay
        setTimeout(() => {
          setFiles(prev => prev.filter(f => !f.uploadSuccess));
        }, 2000);
      }

      if (errorCount > 0 && successCount === 0) {
        setError(`All uploads failed. Please check the errors and try again.`);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear all files
  const clearAllFiles = useCallback(() => {
    setFiles([]);
    setError(null);
    setMessage(null);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Bulk Upload Documents</h3>
            {files.length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs sm:text-sm px-2 py-1 rounded-full flex-shrink-0">
                {files.length} file{files.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* File Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Files *
            </label>
            <div 
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                isDragOver 
                  ? 'border-blue-400 bg-blue-50 scale-105' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="bulk-file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="bulk-file-input"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <div className={`p-4 rounded-full ${
                  isDragOver ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <Upload className={`w-8 h-8 ${
                    isDragOver ? 'text-blue-600' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <p className={`text-lg font-medium ${
                    isDragOver ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    {isDragOver ? 'Drop files here' : 'Drag & drop files here or click to browse'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Select multiple files at once
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, JPEG, PNG
                  </p>
                </div>
              </label>
            </div>
          </div>


          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h4 className="font-medium text-gray-900">Selected Files ({files.length})</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => document.getElementById('bulk-file-input')?.click()}
                    className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Add More Files</span>
                    <span className="sm:hidden">Add</span>
                  </button>
                  <button
                    onClick={() => setShowBulkEdit(!showBulkEdit)}
                    className="text-green-600 hover:text-green-700 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Edit All</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                  <button
                    onClick={clearAllFiles}
                    className="text-red-600 hover:text-red-700 text-xs sm:text-sm flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Clear All</span>
                    <span className="sm:hidden">Clear</span>
                  </button>
                </div>
              </div>

              {/* Table Structure */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-auto">
                  <table className="w-full min-w-[1200px]">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                          File
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Access
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Ownership
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {files.map((fileData) => (
                        <tr
                          key={fileData.id}
                          className={`${
                            fileData.uploadSuccess 
                              ? 'bg-green-50' 
                              : fileData.uploadError 
                              ? 'bg-red-50' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {fileData.file.name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formatFileSize(fileData.file.size)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={fileData.title}
                              onChange={(e) => updateFileProperty(fileData.id, 'title', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={fileData.isUploading}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={fileData.document_type}
                              onChange={(e) => updateFileProperty(fileData.id, 'document_type', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 relative z-10"
                              disabled={fileData.isUploading}
                            >
                              <option value="tax_return">Tax Return</option>
                              <option value="profit_loss_statement">P&L Statement</option>
                              <option value="balance_sheet">Balance Sheet</option>
                              <option value="financial_statement">Financial Statement</option>
                              <option value="bank_statement">Bank Statement</option>
                              <option value="other">Other</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={fileData.access_level}
                              onChange={(e) => updateFileProperty(fileData.id, 'access_level', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 relative z-10"
                              disabled={fileData.isUploading}
                            >
                              <option value="private">Private</option>
                              <option value="role_based">Role Based</option>
                              <option value="shared">Shared</option>
                              <option value="public">Public</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              <button
                                onClick={() => updateFileProperty(fileData.id, 'ownership_type', 'buyer')}
                                className={`px-2 py-1 text-xs rounded ${
                                  fileData.ownership_type === 'buyer'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                disabled={fileData.isUploading}
                              >
                                Buyer
                              </button>
                              <button
                                onClick={() => updateFileProperty(fileData.id, 'ownership_type', 'seller')}
                                className={`px-2 py-1 text-xs rounded ${
                                  fileData.ownership_type === 'seller'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                disabled={fileData.isUploading}
                              >
                                Seller
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={fileData.description}
                              onChange={(e) => updateFileProperty(fileData.id, 'description', e.target.value)}
                              placeholder="Optional"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={fileData.isUploading}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {fileData.isUploading && (
                                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                              )}
                              {fileData.uploadSuccess && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                              {fileData.uploadError && (
                                <AlertCircle className="w-4 h-4 text-red-600" />
                              )}
                              {!fileData.isUploading && !fileData.uploadSuccess && !fileData.uploadError && (
                                <span className="text-xs text-gray-500">Ready</span>
                              )}
                            </div>
                            {fileData.uploadError && (
                              <div className="text-xs text-red-600 mt-1 max-w-32 truncate">
                                {fileData.uploadError}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => removeFile(fileData.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              disabled={fileData.isUploading}
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {message && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">{message}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 border-t bg-gray-50">
          <div className="text-xs sm:text-sm text-gray-600">
            {files.length > 0 && (
              <span>
                {files.filter(f => f.uploadSuccess).length} uploaded, {' '}
                {files.filter(f => f.isUploading).length} uploading, {' '}
                {files.filter(f => f.uploadError).length} failed
              </span>
            )}
          </div>
          
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              disabled={isUploading}
            >
              {files.length > 0 ? 'Close' : 'Cancel'}
            </button>
            
            {files.length > 0 && (
              <button
                onClick={handleBulkUpload}
                disabled={isUploading || files.every(f => f.isUploading || f.uploadSuccess)}
                className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Uploading...</span>
                    <span className="sm:hidden">Uploading</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Upload All Files</span>
                    <span className="sm:hidden">Upload All</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Edit All Files
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type
                  </label>
                  <select
                    value={bulkDocumentType}
                    onChange={(e) => setBulkDocumentType(e.target.value as DocumentType)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="other">Keep Current</option>
                    <option value="tax_return">Tax Return</option>
                    <option value="profit_loss_statement">P&L Statement</option>
                    <option value="balance_sheet">Balance Sheet</option>
                    <option value="financial_statement">Financial Statement</option>
                    <option value="bank_statement">Bank Statement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Access Level
                  </label>
                  <select
                    value={bulkAccessLevel}
                    onChange={(e) => setBulkAccessLevel(e.target.value as DocumentAccessLevel)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="private">Keep Current</option>
                    <option value="role_based">Role Based</option>
                    <option value="shared">Shared</option>
                    <option value="public">Public</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ownership
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setBulkOwnershipType('buyer')}
                      className={`px-3 py-2 text-sm rounded-lg ${
                        bulkOwnershipType === 'buyer'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Buyer
                    </button>
                    <button
                      onClick={() => setBulkOwnershipType('seller')}
                      className={`px-3 py-2 text-sm rounded-lg ${
                        bulkOwnershipType === 'seller'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Seller
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={bulkDescription}
                    onChange={(e) => setBulkDescription(e.target.value)}
                    placeholder="Leave empty to keep current descriptions"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowBulkEdit(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={applyBulkProperties}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Apply to All Files
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
